import { lazy, Suspense, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom'

import { api, onSesionPerdida } from './lib/api'
import { esCaja } from './lib/config'
import { useSesion } from './lib/sesion'
import { iniciarPreferencias, olvidarPreferencias } from './lib/preferencias'
import { Login } from './features/auth/Login'
import { Recuperar } from './features/auth/Recuperar'
import { EntradaCaja } from './features/auth/EntradaCaja'
import { Layout } from './componentes/layout/Layout'
import { Pantalla } from './componentes/ui/Pantalla'
import { AsistenteRegistro } from './features/caja/AsistenteRegistro'
import { EstaCaja } from './features/caja/EstaCaja'
import { SoloConModulo } from './componentes/layout/SoloConModulo'

// El POS y el turno van en el bundle principal, y el resto se carga cuando se
// abre.
//
// No es una optimización de manual: el ejecutable de la caja arranca a la
// mañana en una PC de mostrador que suele ser vieja, y lo primero que tiene
// que estar en pantalla es la venta. El backoffice —el catálogo, la planilla,
// la configuración, el importador con su lector de Excel— es varias veces más
// código que la venta y no se abre nunca desde el mostrador.
import { Pos } from './features/pos/Pos'
import { PanelTurno } from './features/pos/Turno'

const Productos = lazy(() =>
  import('./features/productos/Productos').then((m) => ({ default: m.Productos })),
)
const PlanillaPrecios = lazy(() =>
  import('./features/productos/PlanillaPrecios').then((m) => ({ default: m.PlanillaPrecios })),
)
const Promociones = lazy(() =>
  import('./features/promociones/Promociones').then((m) => ({ default: m.Promociones })),
)
const Ventas = lazy(() =>
  import('./features/ventas/Ventas').then((m) => ({ default: m.Ventas })),
)
const Tablero = lazy(() =>
  import('./features/tablero/Tablero').then((m) => ({ default: m.Tablero })),
)
const Reportes = lazy(() =>
  import('./features/reportes/Reportes').then((m) => ({ default: m.Reportes })),
)
const Turnos = lazy(() =>
  import('./features/turnos/Turnos').then((m) => ({ default: m.Turnos })),
)
const Fiado = lazy(() => import('./features/fiado/Fiado').then((m) => ({ default: m.Fiado })))
const Merma = lazy(() => import('./features/merma/Merma').then((m) => ({ default: m.Merma })))

const Bitacora = lazy(() =>
  import('./features/bitacora/Bitacora').then((m) => ({ default: m.Bitacora })),
)
const Compras = lazy(() =>
  import('./features/compras/Compras').then((m) => ({ default: m.Compras })),
)
const Configuracion = lazy(() =>
  import('./features/config/Configuracion').then((m) => ({ default: m.Configuracion })),
)

// El panel de plataforma va lazy y aparte de todo lo demás: lo abre una
// persona por deploy y no tiene por qué pesar en el bundle que baja la caja
// del mostrador por OTA.
const Plataforma = lazy(() =>
  import('./features/plataforma/Plataforma').then((m) => ({ default: m.Plataforma })),
)

export function App() {
  useConfirmarLaPantalla()

  return (
    <BrowserRouter>
      <Rutas />
    </BrowserRouter>
  )
}

// Le avisa al host de la caja que la pantalla montó bien.
//
// Es la regla 2 del OTA y estaba PROMETIDA en tres comentarios del backend
// —«el SPA llama a /ui/confirmar cuando termina de montar»— y no la llamaba
// nadie: cero referencias en todo el frontend. O sea que TODA instalación por
// OTA se revertía a los sesenta segundos, y el ajuste quedaba además diciendo
// que corría la versión nueva mientras se servía la vieja.
//
// No lo vio nadie por el otro bug: la versión del bundle era «0.1.0» fija, así
// que el OTA nunca activó nada y esta ventana no llegó a abrirse nunca.
//
// Va en la raíz de la aplicación y no adentro del layout, y sin esperar la
// sesión: la ventana es de un minuto y la caja puede quedarse en la pantalla
// del PIN mucho más que eso. Y no lleva token porque el endpoint es anónimo, a
// propósito: tiene que poder confirmar antes de que nadie entre.
function useConfirmarLaPantalla() {
  useEffect(() => {
    if (!esCaja()) return

    // El host contesta 200 aunque no haya ninguna activación esperando, así
    // que llamarlo en cada arranque es inofensivo. Un fallo se ignora: si el
    // host no responde, el rollback es justamente lo que corresponde.
    void fetch('/ui/confirmar', { method: 'POST' }).catch(() => {})
  }, [])
}

function Rutas() {
  const { rehidratar, cargando, usuario } = useSesion()

  // ¿Esta caja ya está atada a un local? Solo tiene sentido en el ejecutable
  // del mostrador —en el navegador el local sale del slug de la URL— y por eso
  // la consulta ni se dispara en los otros runtimes.
  const registro = useQuery({
    queryKey: ['registro'],
    queryFn: () => api.get<{ registrada: boolean }>('/registro'),
    enabled: esCaja(),
    retry: false,
    staleTime: Infinity,
  })

  useEffect(() => {
    void rehidratar()
  }, [rehidratar])

  // Las preferencias de grilla son de la PERSONA y viven en el servidor, así
  // que se piden en cuanto se sabe quién entró —y de nuevo si cambia, que es
  // el relevo de cajero en la tablet del mostrador—. Ver lib/preferencias.
  useEffect(() => {
    if (usuario === null) {
      olvidarPreferencias()
      return
    }

    void iniciarPreferencias(usuario.id)
  }, [usuario])

  // Cuando el refresh falla, el cliente HTTP avisa y acá se redirige. Así
  // ninguna pantalla tiene que saber qué hacer cuando muere la sesión.
  const navegar = useNavigate()
  useEffect(() => {
    const desuscribir = onSesionPerdida(() => {
      const slug = useSesion.getState().tenant?.slug

      // El `void` no es decoración: el navigate de react-router 7 devuelve una
      // promesa que se resuelve cuando la navegación terminó, y acá no hay
      // nada que esperar. Dejarla suelta la haría indistinguible de una
      // promesa que sí había que esperar.
      void navegar(slug ? `/${slug}/login` : '/login', { replace: true })
    })

    // Se envuelve en una llave porque el cleanup de un efecto no puede
    // devolver un valor, y la baja de la suscripción devuelve un booleano.
    return () => {
      desuscribir()
    }
  }, [navegar])

  if (cargando) return <Pantalla.Cargando />

  // En la caja las rutas son otras y es a propósito.
  //
  // No hay slug en la URL: el ejecutable está registrado contra UN local y
  // meter el slug en la dirección solo daría lugar a que alguien la edite. Y
  // la pantalla de inicio es el POS, no el tablero: la caja se abre a la
  // mañana y se queda ocho horas en la pantalla de venta.
  if (esCaja()) {
    // El primer arranque: esta caja todavía no está atada a ningún local.
    //
    // Va ANTES del login y no adentro, porque sin registro no hay a quién
    // preguntarle el PIN: la lista de empleados la sirve la nube. Hasta que
    // esto existió, estrenar una caja era editar el appsettings.json y
    // mandarle dos POST a mano al host local —los endpoints estaban desde el
    // principio y no los llamaba ninguna pantalla—.
    if (registro.data?.registrada === false) {
      return <AsistenteRegistro onListo={() => void registro.refetch()} />
    }

    // Mientras no se sabe, no se pinta ninguna de las dos. Con el `?.` de
    // arriba a secas, una caja SIN registrar mostraba la entrada con PIN —«no
    // hay nadie habilitado», que manda a cargar un empleado en un local que
    // todavía no se eligió— hasta que la consulta respondiera. Es la misma
    // regla que el selector del POS: arrancar con la pantalla equivocada
    // manda a resolver el problema al lugar equivocado.
    if (registro.isPending) return <Pantalla.Cargando />

    if (!usuario) return <EntradaCaja />

    return (
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Pos />} />
          <Route path="turno" element={<PanelTurno />} />

          {/* Los equipos de ESTA máquina. Va solo acá y no en la web: la
              balanza y la térmica son de la computadora, no del local, y en
              un navegador no hay puerto serie que configurar. */}
          <Route path="equipo" element={<EstaCaja />} />

          {/* Y Gestión entera, las MISMAS pantallas que el navegador.
              Viajaban adentro de este bundle desde siempre y no tenían a
              quién preguntarle, así que el router las mandaba de vuelta al
              POS: cargar un producto o cambiar un precio desde la PC del
              local significaba abandonar el sistema y abrir un navegador.
              Sus pedidos salen a la nube por el puente del host local (ver
              `base` en lib/api.ts), así que piden internet —y lo dicen
              cuando no hay— mientras la venta sigue andando offline. */}
          {rutasDeGestion(true)}

          <Route path="*" element={<Pantalla.NoEncontrada />} />
        </Route>
      </Routes>
    )
  }

  return (
    <Routes>
      {/* La administración de la PLATAFORMA. No cuelga del slug de ningún
          local y tiene su propia sesión: el token no lleva tenant y no sirve
          para ningún endpoint de un local (ver lib/plataforma). Va antes que
          la ruta del slug porque «plataforma» está en la lista de nombres
          reservados justamente para esto. */}
      <Route path="/plataforma/*" element={<Cargable><Plataforma /></Cargable>} />

      <Route path="/login" element={<Login />} />
      <Route path="/:slug/login" element={<Login />} />

      {/* Recuperar la contraseña. Va sin sesión y ANTES de la ruta del slug,
          igual que el login: el enlace del mail lo abre alguien que
          justamente no puede entrar. */}
      <Route path="/:slug/recuperar" element={<Recuperar />} />

      {/* Toda ruta del sistema va bajo el slug del local: es lo que permite
          guardar en favoritos la dirección de tu propia verdulería. */}
      <Route path="/:slug" element={usuario ? <Layout /> : <ALogin />}>
        {/* La caja web: el mismo POS, sin balanza ni impresora térmica. El
            requisito era que la web pueda hacer todo, y vender es lo primero
            de ese todo. */}
        <Route path="caja" element={<Pos />} />
        <Route path="turno" element={<PanelTurno />} />

        {rutasDeGestion(false)}

        <Route path="*" element={<Pantalla.NoEncontrada />} />
      </Route>

      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Pantalla.NoEncontrada />} />
    </Routes>
  )
}

/**
 * Las pantallas de GESTIÓN, que son las mismas en los dos runtimes.
 *
 * Están acá y no en un archivo aparte a propósito: el test que exige que toda
 * pantalla tenga su artículo de ayuda (§1.5) lee los `path=` de ESTE archivo,
 * así que una ruta mudada a otro lado sería una pantalla que puede quedarse
 * sin ayuda sin que nada falle.
 *
 * `enCaja` cambia una sola cosa y es la puerta de entrada: en el navegador el
 * tablero es el índice del local, y en el ejecutable el índice es la venta, así
 * que el tablero tiene su propia dirección. Todo lo demás es idéntico —la misma
 * ruta, el mismo componente— y lo que cambia es a quién le pregunta, que lo
 * resuelve el cliente HTTP por el área (ver `base` en lib/api.ts).
 */
function rutasDeGestion(enCaja: boolean) {
  return (
    <>
      {enCaja ? (
        <Route path="inicio" element={<Cargable><Tablero /></Cargable>} />
      ) : (
        <Route index element={<Cargable><Tablero /></Cargable>} />
      )}

      {/* El fiado: el saldo es la suma de los movimientos de TODAS las cajas
          más los pagos del escritorio, así que sale de la nube. La caja SÍ
          puede fiar sin internet (los clientes bajan por sync), pero cobrar y
          ver cuentas necesita conexión. */}
      <Route
        path="fiado"
        element={
          <SoloConModulo pantalla="fiado">
            <Cargable>
              <Fiado />
            </Cargable>
          </SoloConModulo>
        }
      />

      {/* La merma se DECLARA desde el turno (la caja tiene su propio camino,
          que anda sin internet) y acá se MIRA: es el reporte que dice qué
          proporción de cada producto se termina tirando, y para eso hace falta
          el costo, que no baja a la caja. */}
      <Route
        path="merma"
        element={
          <SoloConModulo pantalla="merma">
            <Cargable>
              <Merma />
            </Cargable>
          </SoloConModulo>
        }
      />

      {/* Las compras mueven el costo del catálogo, que es el dato más sensible
          del negocio y no baja a la caja: el remito se carga contra la nube. */}
      <Route
        path="compras"
        element={
          <SoloConModulo pantalla="compras">
            <Cargable>
              <Compras />
            </Cargable>
          </SoloConModulo>
        }
      />

      <Route
        path="reportes"
        element={
          <Cargable>
            <Reportes />
          </Cargable>
        }
      />

      {/* El control de caja: los turnos de TODAS las cajas del local. Por eso
          se consulta contra la nube y no contra la base local, que conoce
          solamente los de esta máquina. */}
      <Route
        path="turnos"
        element={
          <Cargable>
            <Turnos />
          </Cargable>
        }
      />

      {/* Las ventas se consultan contra la nube, que es donde están todas: la
          caja del mostrador solo tiene las suyas, y ni siquiera las de los días
          que ya purgó de su cola. */}
      <Route
        path="ventas"
        element={
          <Cargable>
            <Ventas />
          </Cargable>
        }
      />
      <Route
        path="productos"
        element={
          <Cargable>
            <Productos />
          </Cargable>
        }
      />
      <Route
        path="precios"
        element={
          <Cargable>
            <PlanillaPrecios />
          </Cargable>
        }
      />
      <Route
        path="promociones"
        element={
          <SoloConModulo pantalla="promociones">
            <Cargable>
              <Promociones />
            </Cargable>
          </SoloConModulo>
        }
      />

      {/* La bitácora: pantalla del dueño, con su permiso propio de solo
          lectura. Se lee de la nube, que es donde se anota lo de todas las
          cajas. */}
      <Route
        path="bitacora"
        element={
          <Cargable>
            <Bitacora />
          </Cargable>
        }
      />

      <Route
        path="config/*"
        element={
          <Cargable>
            <Configuracion />
          </Cargable>
        }
      />
    </>
  )
}

// Envoltorio de las pantallas que se cargan por demanda.
//
// El indicador es discreto a propósito: el chunk pesa unos pocos KB y en una
// conexión normal no se llega a ver. Lo que no puede pasar es que la pantalla
// quede en blanco sin explicación en la conexión del local.
function Cargable({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="grid min-h-[40vh] place-items-center">
          <span className="text-[13.5px] text-tinta-tenue">Cargando la pantalla…</span>
        </div>
      }
    >
      {children}
    </Suspense>
  )
}

// Redirige al login conservando el slug de la URL: sin esto, entrar a una
// dirección profunda sin sesión te manda al login genérico y perdés de qué
// local eras.
function ALogin() {
  const { slug } = useParams<{ slug: string }>()
  return <Navigate to={slug ? `/${slug}/login` : '/login'} replace />
}
